import type {
  CreateGuaranteedServiceRequest,
  CreateGuaranteedServiceResponse,
  DeleteGuaranteedServiceResponse,
  GuaranteedProbeSpendResponse,
  GuaranteedServiceEntry,
  GuaranteedServicePropagationResponse,
  ListGuaranteedServiceEntriesResponse,
  ListGuaranteedServiceEventsResponse,
  ListGuaranteedServiceTextSkusResponse,
  PatchGuaranteedServiceRequest,
  PatchGuaranteedServiceResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const ROOT = ['guaranteed-service'] as const;
/**
 * The list has its own key (#2658): writes used to invalidate ROOT, which
 * also refetched text-skus, events and probe-spend after every click. Writes
 * now write the server's returned row into this key and refetch only it (and
 * events, which a mode switch can append to).
 */
export const GUARANTEED_LIST_KEY = [...ROOT, 'list'] as const;
const EVENTS_KEY = [...ROOT, 'events'] as const;
// Explicit so every tenant-routing SKU pool panel that mounts on a SKU switch
// shares one read instead of refetching.
const LIST_STALE_MS = 30_000;
/** The text-SKU catalog changes on SKU launches, not while an operator edits. */
const TEXT_SKUS_STALE_MS = 5 * 60_000;

export function useGuaranteedServiceList(): UseQueryResult<ListGuaranteedServiceEntriesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: GUARANTEED_LIST_KEY,
    queryFn: () => opsApiRequest<ListGuaranteedServiceEntriesResponse>(
      '/v1/ops/guaranteed-service',
      {},
      fetchImpl,
    ),
    staleTime: LIST_STALE_MS,
  });
}

export interface GuaranteedEventsQuery {
  readonly page: number;
  readonly skuId: string;
  readonly upstreamId: string;
  readonly kind: string;
  /** R1-B10: `trip_reason` of trips / releases; empty = every kind. */
  readonly faultKind?: string;
}

export function useGuaranteedEvents(
  params: GuaranteedEventsQuery,
): UseQueryResult<ListGuaranteedServiceEventsResponse> {
  const { fetchImpl } = useSession();
  const search = new URLSearchParams();
  search.set('page', String(params.page));
  search.set('page_size', '20');
  if (params.skuId.length > 0) search.set('sku_id', params.skuId);
  if (params.upstreamId.length > 0) search.set('upstream_id', params.upstreamId);
  if (params.kind.length > 0) search.set('kind', params.kind);
  if (params.faultKind !== undefined && params.faultKind.length > 0) search.set('fault_kind', params.faultKind);
  const qs = search.toString();
  return useQuery({
    queryKey: [...EVENTS_KEY, params.page, params.skuId, params.upstreamId, params.kind, params.faultKind ?? ''],
    queryFn: () => opsApiRequest<ListGuaranteedServiceEventsResponse>(
      `/v1/ops/guaranteed-service/events?${qs}`,
      {},
      fetchImpl,
    ),
  });
}

/** 探针花费 (ops read; only fetched while the page is open). */
export function useGuaranteedProbeSpend(hours: number): UseQueryResult<GuaranteedProbeSpendResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ROOT, 'probe-spend', hours],
    queryFn: () => opsApiRequest<GuaranteedProbeSpendResponse>(
      `/v1/ops/guaranteed-service/probe-spend?hours=${String(hours)}`,
      {},
      fetchImpl,
    ),
    staleTime: LIST_STALE_MS,
  });
}

export function useGuaranteedServiceTextSkus(): UseQueryResult<ListGuaranteedServiceTextSkusResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ROOT, 'text-skus'],
    queryFn: () => opsApiRequest<ListGuaranteedServiceTextSkusResponse>(
      '/v1/ops/guaranteed-service/text-skus',
      {},
      fetchImpl,
    ),
    staleTime: TEXT_SKUS_STALE_MS,
  });
}

function upsertEntries(
  current: ListGuaranteedServiceEntriesResponse | undefined,
  items: readonly GuaranteedServiceEntry[],
): ListGuaranteedServiceEntriesResponse | undefined {
  if (current === undefined) return current;
  const byId = new Map(items.map((item) => [item.id, item]));
  const sku = current.sku.map((row) => {
    const next = byId.get(row.id);
    return next !== undefined && next.scope === 'sku' ? next : row;
  });
  const orgSku = current.org_sku.map((row) => {
    const next = byId.get(row.id);
    return next !== undefined && next.scope === 'org_sku' ? next : row;
  });
  const known = new Set([...current.sku, ...current.org_sku].map((row) => row.id));
  const added = items.filter((item) => !known.has(item.id));
  return {
    sku: [...added.filter((item) => item.scope === 'sku'), ...sku],
    org_sku: [...added.filter((item) => item.scope === 'org_sku'), ...orgSku],
  };
}

function withoutEntry(
  current: ListGuaranteedServiceEntriesResponse | undefined,
  id: string,
): ListGuaranteedServiceEntriesResponse | undefined {
  if (current === undefined) return current;
  return {
    sku: current.sku.filter((row) => row.id !== id),
    org_sku: current.org_sku.filter((row) => row.id !== id),
  };
}

/**
 * Server truth first, then one refetch of the list. An in-flight list read
 * started before this write committed; landing after the row was replaced it
 * would put the pre-write value back. The explicit cancel makes that
 * independent of `invalidateQueries`' default `cancelRefetch`, which today
 * also discards it (the race test passes with either one alone).
 */
async function applyWriteResult(
  qc: QueryClient,
  update: (current: ListGuaranteedServiceEntriesResponse | undefined) => ListGuaranteedServiceEntriesResponse | undefined,
): Promise<void> {
  await qc.cancelQueries({ queryKey: GUARANTEED_LIST_KEY });
  qc.setQueryData<ListGuaranteedServiceEntriesResponse>(GUARANTEED_LIST_KEY, update);
  void qc.invalidateQueries({ queryKey: GUARANTEED_LIST_KEY });
  void qc.invalidateQueries({ queryKey: EVENTS_KEY });
}

/** A failed write re-reads the list so no control keeps a value the server refused. */
function refetchAfterFailure(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: GUARANTEED_LIST_KEY });
}

export function useCreateGuaranteedService(): UseMutationResult<
  CreateGuaranteedServiceResponse,
  Error,
  CreateGuaranteedServiceRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => opsApiRequest<CreateGuaranteedServiceResponse>(
      '/v1/ops/guaranteed-service',
      { method: 'POST', body },
      fetchImpl,
    ),
    onSuccess: (response) => applyWriteResult(qc, (current) => upsertEntries(current, response.items)),
    onError: () => refetchAfterFailure(qc),
  });
}

export function usePatchGuaranteedService(): UseMutationResult<
  PatchGuaranteedServiceResponse,
  Error,
  { readonly id: string; readonly body: PatchGuaranteedServiceRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => opsApiRequest<PatchGuaranteedServiceResponse>(
      `/v1/ops/guaranteed-service/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
      fetchImpl,
    ),
    onSuccess: (response) => applyWriteResult(qc, (current) => upsertEntries(current, [response.item])),
    onError: () => refetchAfterFailure(qc),
  });
}

export function useDeleteGuaranteedService(): UseMutationResult<
  DeleteGuaranteedServiceResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => opsApiRequest<DeleteGuaranteedServiceResponse>(
      `/v1/ops/guaranteed-service/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
      fetchImpl,
    ),
    onSuccess: (_response, id) => applyWriteResult(qc, (current) => withoutEntry(current, id)),
    onError: () => refetchAfterFailure(qc),
  });
}

/**
 * Tokyo / Shanghai read-back of one entry (#2658). Not a React Query read: the
 * write-status hook polls it on its own schedule and stops on a verdict.
 */
export async function fetchGuaranteedPropagation(
  id: string,
  expectedUpdatedAt: string | null,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GuaranteedServicePropagationResponse> {
  const qs = expectedUpdatedAt === null ? '' : `?updated_at=${encodeURIComponent(expectedUpdatedAt)}`;
  return opsApiRequest<GuaranteedServicePropagationResponse>(
    `/v1/ops/guaranteed-service/${encodeURIComponent(id)}/propagation${qs}`,
    { signal },
    fetchImpl,
  );
}
